import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { formatDateTime } from "../lib/dateTime";
import { Button } from "@/components/ui/button";
import { PageLoadError } from "./NoAccess";
import { Loader2, RefreshCcw, CheckCircle2, XCircle, Webhook } from "lucide-react";

export default function WebhooksMonitor() {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get("/super-admin/webhooks/events");
      setData(r.data);
    } catch (e) { setLoadError(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="webhooks-monitor">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("wh.title")}</h1>
          <p className="text-slate-500">{t("wh.subtitle")}</p>
        </div>
        <Button onClick={load} variant="outline" className="border-slate-200 text-slate-800/70" data-testid="reload-webhooks">
          <RefreshCcw className="w-4 h-4 mr-2" /> {t("wh.reload")}
        </Button>
      </div>

      <PageLoadError error={loading ? null : loadError} onRetry={load} testId="webhooks-load-error" />
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-amber-600" /> : loadError || !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">{t("wh.total")}</div>
              <div className="font-sans font-semibold text-3xl text-amber-600">{data.total}</div>
            </div>
            {(data.by_type || []).slice(0, 3).map((b) => (
              <div key={b.type} className="rounded-2xl bg-white border border-slate-200 p-5">
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-1 truncate">{b.type}</div>
                <div className="font-sans font-semibold text-3xl">{b.count}</div>
                <div className="text-xs text-emerald-600">{t("wh.handled", { n: b.handled })}</div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2">
              <Webhook className="w-4 h-4 text-amber-600" />
              <h3 className="font-sans font-semibold">{t("wh.recent")}</h3>
            </div>
            <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
              {(data.stripe || []).map((e) => (
                <div key={e.event_id} className="px-6 py-3 flex items-center gap-3 text-sm" data-testid={`webhook-${e.event_id}`}>
                  {e.handled ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-rose-400" />}
                  <code className="text-xs text-amber-600">{e.type}</code>
                  <code className="text-xs text-slate-400 flex-1">{e.event_id}</code>
                  <span className="text-xs text-slate-400">{formatDateTime(e.received_at, lang)}</span>
                </div>
              ))}
              {data.stripe?.length === 0 && <p className="px-6 py-10 text-slate-400 text-center text-sm">{t("wh.empty")}</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
